/* Live Natural — theme.js
   Handles cart drawer, mobile menu, variant selection, quantity, AJAX cart.
*/
(function () {
  'use strict';

  /* ----------------------------
     Helpers
     ---------------------------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const formatMoney = (cents, currency = 'CAD') => {
    const value = (cents / 100).toFixed(2);
    return `$${value} ${currency}`;
  };

  /* ----------------------------
     Cart drawer
     ---------------------------- */
  const cartDrawer = $('#cart-drawer');
  const cartDrawerBody = $('#cart-drawer-body');

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-cart]')) {
      e.preventDefault();
      openCartDrawer();
    }
    if (e.target.closest('[data-close-drawer]')) {
      closeCartDrawer();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeCartDrawer();
      closeMobileMenu();
    }
  });

  /* ----------------------------
     Mobile menu
     ---------------------------- */
  const mobileMenu = $('#mobile-menu');

  function openMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.add('is-open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('is-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-menu]')) {
      e.preventDefault();
      openMobileMenu();
    }
    if (e.target.closest('[data-close-menu]')) {
      closeMobileMenu();
    }
  });

  /* ----------------------------
     Cart updates (AJAX)
     ---------------------------- */
  function updateCartCount(count) {
    $$('.cart-count').forEach(el => {
      el.textContent = count;
      el.setAttribute('data-count', count);
    });
  }

  async function refreshCartDrawer() {
    try {
      const res = await fetch('/?section_id=cart-drawer-section');
      if (!res.ok) return;
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContents = doc.querySelector('[data-cart-drawer-contents]');
      if (newContents && cartDrawerBody) {
        cartDrawerBody.innerHTML = newContents.innerHTML;
      }
    } catch (err) {
      // Fallback: full reload
      window.location.reload();
    }
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) { return null; }
  }

  /* ----------------------------
     Add to cart
     ---------------------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    e.preventDefault();

    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding…';
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.description || 'Could not add item');
      }

      const cart = await fetchCart();
      if (cart) updateCartCount(cart.item_count);

      await refreshCartDrawer();
      openCartDrawer();

      if (submitBtn) {
        submitBtn.textContent = 'Added ✓';
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 1200);
      }
    } catch (err) {
      console.error(err);
      if (submitBtn) {
        submitBtn.textContent = 'Try again';
        submitBtn.disabled = false;
      }
      alert(err.message || 'Something went wrong. Please try again.');
    }
  });

  /* ----------------------------
     Cart drawer item updates
     ---------------------------- */
  document.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const key = removeBtn.dataset.cartRemove;
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        const cart = await fetchCart();
        if (cart) updateCartCount(cart.item_count);
        await refreshCartDrawer();
      } catch (err) {
        window.location.reload();
      }
    }
  });

  /* ----------------------------
     Product variant selection
     ---------------------------- */
  const productForm = $('[data-product-form]');
  if (productForm) {
    const variantData = $('[data-product-variants]');
    let variants = [];
    if (variantData) {
      try { variants = JSON.parse(variantData.textContent); }
      catch (e) { variants = []; }
    }

    const variantInput = productForm.querySelector('[name="id"]');
    const priceEl = $('[data-product-price]');
    const optionGroups = $$('[data-option-group]', productForm);

    function getSelectedVariant() {
      const selected = optionGroups.map(group => {
        const active = group.querySelector('.is-selected');
        return active ? active.dataset.value : null;
      });
      return variants.find(v => {
        return v.options.every((opt, i) => opt === selected[i]);
      });
    }

    function updateVariant() {
      const variant = getSelectedVariant();
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      if (priceEl) {
        priceEl.innerHTML = '';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const del = document.createElement('del');
          del.textContent = formatMoney(variant.compare_at_price);
          priceEl.appendChild(del);
        }
        priceEl.appendChild(document.createTextNode(formatMoney(variant.price)));
      }
      const submitBtn = productForm.querySelector('[type="submit"]');
      if (submitBtn) {
        if (!variant.available) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Sold Out';
        } else {
          submitBtn.disabled = false;
          submitBtn.textContent = submitBtn.dataset.defaultText || 'Add to Cart';
        }
      }
    }

    optionGroups.forEach(group => {
      group.addEventListener('click', (e) => {
        const opt = e.target.closest('[data-value]');
        if (!opt) return;
        $$('[data-value]', group).forEach(o => o.classList.remove('is-selected'));
        opt.classList.add('is-selected');
        updateVariant();
      });
    });
  }

  /* ----------------------------
     Quantity controls
     ---------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const wrap = btn.closest('.product__quantity, .qty-control');
    if (!wrap) return;
    const input = wrap.querySelector('input[type="number"]');
    if (!input) return;
    const dir = btn.dataset.qty;
    let val = parseInt(input.value, 10) || 1;
    val = dir === 'up' ? val + 1 : Math.max(1, val - 1);
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ----------------------------
     Product gallery thumbnails
     ---------------------------- */
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('.product__thumb');
    if (!thumb) return;
    const main = $('.product__main-image img');
    const targetSrc = thumb.dataset.src || thumb.querySelector('img')?.src;
    if (main && targetSrc) {
      main.src = targetSrc;
      $$('.product__thumb').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    }
  });

  /* ----------------------------
     Initial sync
     ---------------------------- */
  fetchCart().then(cart => {
    if (cart) updateCartCount(cart.item_count);
  });
})();
